import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Receipt,
  Calculator,
  RefreshCw,
  ExternalLink,
  Pencil,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Euro,
  User,
  MapPin,
  Building2,
  Mail,
  Download,
  Zap,
  BadgeEuro,
  Send,
  MessageSquare,
} from "lucide-react";
import { getTenants, getTenant, updateTenant, getChargesByYear, sendEmailRegulation } from "@/lib/api";
import { toast } from "sonner";

const YEARS = ["2022", "2023", "2024", "2025", "2026"];

const CATEGORY_LABELS = {
  tax: "Taxe foncière / Impôts",
  expense: "Dépense (électricité…)",
  maintenance: "Maintenance",
  other: "Autre",
};

export default function Charges() {
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState("");
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tenantLoading, setTenantLoading] = useState(false);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editCharges, setEditCharges] = useState("");

  const [regulYear, setRegulYear] = useState(String(new Date().getFullYear() - 1));
  const [chargesReelles, setChargesReelles] = useState("");
  const [regulResult, setRegulResult] = useState(null);

  const [txLoading, setTxLoading] = useState(false);
  const [txCharges, setTxCharges] = useState([]);
  const [txSelected, setTxSelected] = useState({});

  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailNote, setEmailNote] = useState("");

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await getTenants();
        setTenants((res.data || []).sort((a, b) => a.name.localeCompare(b.name)));
      } catch {
        toast.error("Erreur lors du chargement des locataires");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!selectedTenantId) {
      setTenant(null);
      setRegulResult(null);
      setChargesReelles("");
      setTxCharges([]);
      setTxSelected({});
      return;
    }
    (async () => {
      setTenantLoading(true);
      try {
        const res = await getTenant(selectedTenantId);
        setTenant(res.data);
        setEditCharges(String(res.data.charges_provisionnees || 0));
        setRegulResult(null);
        setChargesReelles("");
        setTxCharges([]);
        setTxSelected({});
      } catch {
        toast.error("Erreur lors du chargement du locataire");
      } finally {
        setTenantLoading(false);
      }
    })();
  }, [selectedTenantId]);

  const handleUpdateCharges = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    try {
      const res = await updateTenant(selectedTenantId, {
        charges_provisionnees: parseFloat(editCharges) || 0,
      });
      setTenant(res.data);
      setEditDialogOpen(false);
      setRegulResult(null);
      toast.success("Charges provisionnées mises à jour");
    } catch {
      toast.error("Erreur lors de la mise à jour");
    } finally {
      setEditLoading(false);
    }
  };

  const handleImportTx = async () => {
    setTxLoading(true);
    try {
      const res = await getChargesByYear(regulYear);
      const txs = res.data.transactions || [];
      setTxCharges(txs);
      const sel = {};
      txs.forEach((tx) => { sel[tx.id] = true; });
      setTxSelected(sel);
      if (txs.length === 0) {
        toast.info(`Aucune transaction de charges trouvée pour ${regulYear}`);
      } else {
        toast.success(`${txs.length} transaction(s) trouvée(s) pour ${regulYear}`);
      }
    } catch {
      toast.error("Erreur lors de la récupération des transactions");
    } finally {
      setTxLoading(false);
    }
  };

  const txSelectedTotal = txCharges
    .filter((tx) => txSelected[tx.id])
    .reduce((s, tx) => s + Math.abs(tx.amount), 0);

  const handleUseTxTotal = () => {
    setChargesReelles(String(txSelectedTotal.toFixed(2)));
    setRegulResult(null);
    toast.success("Montant importé depuis les transactions");
  };

  const handleCalculerRegul = () => {
    const provisionnees = (tenant?.charges_provisionnees || 0) * 12;
    const reelles = parseFloat(chargesReelles) || 0;
    const solde = reelles - provisionnees;
    setRegulResult({ provisionnees, reelles, solde });
    setEmailNote("");
  };

  const handleSendEmail = async () => {
    if (!regulResult) return;
    setEmailLoading(true);
    try {
      const res = await sendEmailRegulation({
        tenant_id: selectedTenantId,
        year: parseInt(regulYear),
        charges_provisionnees: regulResult.provisionnees,
        charges_reelles: regulResult.reelles,
        solde: regulResult.solde,
        message_libre: emailNote.trim() || null,
      });
      toast.success(`Email envoyé à ${res.data.to}`);
      setEmailDialogOpen(false);
      setEmailNote("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l\'envoi de l\'email");
    } finally {
      setEmailLoading(false);
    }
  };

  const formatCurrency = (amount) =>
    new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(amount || 0);

  const formatDate = (d) =>
    new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });

  const loyer = tenant?.rent_amount || 0;
  const charges = tenant?.charges_provisionnees || 0;
  const total = loyer + charges;

  const totalChargesMois = tenants.reduce((s, t) => s + (t.charges_provisionnees || 0), 0);
  const totalLoyersMois = tenants.reduce((s, t) => s + (t.rent_amount || 0), 0);
  const nbAvecCharges = tenants.filter((t) => (t.charges_provisionnees || 0) > 0).length;

  return (
    <div className="space-y-8 animate-fade-in">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: "Manrope" }}>
          Charges Provisionnées
        </h1>
        <p className="text-slate-500 mt-1">Consultez et régularisez les charges par locataire</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-sky-100 flex items-center justify-center">
              <Receipt className="w-6 h-6 text-sky-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-sky-700" style={{ fontFamily: "Manrope" }}>
                {formatCurrency(totalChargesMois)}
              </p>
              <p className="text-sm text-slate-500">Charges totales / mois</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-navy-100 flex items-center justify-center">
              <Euro className="w-6 h-6 text-navy-700" />
            </div>
            <div>
              <p className="text-2xl font-bold text-navy-700" style={{ fontFamily: "Manrope" }}>
                {formatCurrency(totalLoyersMois + totalChargesMois)}
              </p>
              <p className="text-sm text-slate-500">Total loyers CC / mois</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
              <User className="w-6 h-6 text-slate-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope" }}>
                {nbAvecCharges} / {tenants.length}
              </p>
              <p className="text-sm text-slate-500">Locataires avec charges</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle style={{ fontFamily: "Manrope" }}>Sélectionner un locataire</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center gap-2 text-slate-500">
              <RefreshCw className="w-4 h-4 animate-spin" />
              Chargement des locataires…
            </div>
          ) : (
            <div className="max-w-lg">
              <Label className="mb-2 block text-slate-700">Locataire</Label>
              <Select value={selectedTenantId} onValueChange={setSelectedTenantId}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Choisir un locataire…" />
                </SelectTrigger>
                <SelectContent>
                  {tenants.map((t) => (
                    <SelectItem key={t.id} value={t.id}>
                      <span className="font-medium">{t.name}</span>
                      <span className="ml-2 text-slate-400 text-sm">— {t.property_address}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </CardContent>
      </Card>

      {tenantLoading && (
        <div className="flex items-center justify-center py-16">
          <RefreshCw className="w-8 h-8 animate-spin text-navy-900" />
        </div>
      )}

      {!tenantLoading && tenant && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center justify-between" style={{ fontFamily: "Manrope" }}>
                  <span>Locataire</span>
                  <Link to={`/tenants/${tenant.id}`}>
                    <Button variant="ghost" size="sm">
                      <ExternalLink className="w-4 h-4 mr-1" />Fiche
                    </Button>
                  </Link>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-xl font-bold text-slate-600">
                      {tenant.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900 text-lg">{tenant.name}</p>
                    {tenant.structure && tenant.structure !== "Non défini" && (
                      <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                        {tenant.structure}
                      </span>
                    )}
                  </div>
                </div>
                <div className="space-y-2 pt-2 border-t border-slate-100">
                  <div className="flex items-center gap-3">
                    <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <p className="text-sm text-slate-700">{tenant.property_address}</p>
                  </div>
                  {tenant.structure && tenant.structure !== "Non défini" && (
                    <div className="flex items-center gap-3">
                      <Building2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <p className="text-sm text-slate-700">{tenant.structure}</p>
                    </div>
                  )}
                  {tenant.phone && (
                    <div className="flex items-center gap-3">
                      <MessageSquare className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <p className="text-sm text-slate-700">{tenant.phone}</p>
                    </div>
                  )}
                  {tenant.email && (
                    <div className="flex items-center gap-3">
                      <Mail className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      <p className="text-sm text-slate-700">{tenant.email}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="flex items-center justify-between" style={{ fontFamily: "Manrope" }}>
                  <span className="flex items-center gap-2">
                    <Calculator className="w-5 h-5 text-slate-600" />
                    Décomposition mensuelle
                  </span>
                  <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm">
                        <Pencil className="w-3 h-3 mr-1" />Modifier
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle style={{ fontFamily: "Manrope" }}>
                          Modifier les charges provisionnées
                        </DialogTitle>
                        <DialogDescription>
                          Montant mensuel pour {tenant.name}
                        </DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleUpdateCharges} className="space-y-4">
                        <div className="space-y-2">
                          <Label>Loyer hors charges</Label>
                          <p className="text-sm font-medium bg-slate-50 px-3 py-2 rounded-md">
                            {formatCurrency(loyer)}
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="edit-charges">Charges provisionnées (€ / mois)</Label>
                          <Input
                            id="edit-charges"
                            type="number"
                            step="0.01"
                            min="0"
                            value={editCharges}
                            onChange={(e) => setEditCharges(e.target.value)}
                          />
                        </div>
                        <div className="p-3 bg-navy-50 rounded-lg border border-navy-200">
                          <p className="text-sm text-navy-700">
                            Total CC :{" "}
                            <span className="font-bold">
                              {formatCurrency(loyer + (parseFloat(editCharges) || 0))}
                            </span>
                          </p>
                        </div>
                        <div className="flex justify-end gap-3">
                          <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                            Annuler
                          </Button>
                          <Button
                            type="submit"
                            className="bg-navy-900 hover:bg-navy-800"
                            disabled={editLoading}
                          >
                            {editLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Enregistrer
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <span className="text-sm text-slate-600">Loyer hors charges</span>
                  <span className="font-semibold text-slate-900">{formatCurrency(loyer)}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-sky-50 rounded-lg border border-sky-100">
                  <span className="text-sm text-sky-700 flex items-center gap-2">
                    <Receipt className="w-4 h-4" />Charges provisionnées / mois
                  </span>
                  <span className="font-semibold text-sky-700">{formatCurrency(charges)}</span>
                </div>
                <div className="border-t border-dashed border-slate-200" />
                <div className="flex items-center justify-between p-3 bg-navy-50 rounded-lg border border-navy-200">
                  <span className="font-medium text-navy-900">Total CC / mois</span>
                  <span className="font-bold text-navy-800 text-xl" style={{ fontFamily: "Manrope" }}>
                    {formatCurrency(total)}
                  </span>
                </div>
                <div className="flex items-center justify-between px-3 py-1 text-sm text-slate-500">
                  <span>Estimation charges annuelles</span>
                  <span className="font-medium text-slate-700">{formatCurrency(charges * 12)}</span>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2" style={{ fontFamily: "Manrope" }}>
                <BadgeEuro className="w-5 h-5 text-slate-700" />
                Régulation annuelle des charges
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 items-end">
                <div className="space-y-2">
                  <Label>Année</Label>
                  <Select value={regulYear} onValueChange={(v) => { setRegulYear(v); setRegulResult(null); setTxCharges([]); setTxSelected({}); }}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {YEARS.map((y) => (
                        <SelectItem key={y} value={y}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Depuis les transactions</Label>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={handleImportTx}
                    disabled={txLoading}
                  >
                    {txLoading
                      ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      : <Download className="w-4 h-4 mr-2" />}
                    Récupérer flux
                  </Button>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="charges-reelles">Charges réelles (€)</Label>
                  <Input
                    id="charges-reelles"
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="ex: 1 200"
                    value={chargesReelles}
                    onChange={(e) => { setChargesReelles(e.target.value); setRegulResult(null); }}
                  />
                </div>
                <div>
                  <Button
                    onClick={handleCalculerRegul}
                    disabled={!chargesReelles}
                    className="w-full bg-navy-900 hover:bg-navy-800"
                  >
                    <Calculator className="w-4 h-4 mr-2" />Calculer
                  </Button>
                </div>
              </div>

              {txCharges.length > 0 && (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="bg-slate-50 px-4 py-3 flex items-center justify-between">
                    <p className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                      <Zap className="w-4 h-4 text-sky-500" />
                      {txCharges.length} transaction(s) de charges — {regulYear}
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleUseTxTotal}
                      className="text-navy-700 border-navy-300 hover:bg-navy-50"
                    >
                      Utiliser total ({formatCurrency(txSelectedTotal)})
                    </Button>
                  </div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200 bg-white">
                        <th className="py-2 px-3 w-8">
                          <input
                            type="checkbox"
                            checked={txCharges.every((tx) => txSelected[tx.id])}
                            onChange={(e) => {
                              const sel = {};
                              txCharges.forEach((tx) => { sel[tx.id] = e.target.checked; });
                              setTxSelected(sel);
                            }}
                          />
                        </th>
                        <th className="py-2 px-3 text-left text-slate-500 font-medium">Date</th>
                        <th className="py-2 px-3 text-left text-slate-500 font-medium">Description</th>
                        <th className="py-2 px-3 text-left text-slate-500 font-medium">Catégorie</th>
                        <th className="py-2 px-3 text-right text-slate-500 font-medium">Montant</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {txCharges.map((tx) => (
                        <tr
                          key={tx.id}
                          className={txSelected[tx.id] ? "bg-white" : "bg-slate-50 opacity-50"}
                        >
                          <td className="py-2 px-3">
                            <input
                              type="checkbox"
                              checked={!!txSelected[tx.id]}
                              onChange={(e) =>
                                setTxSelected((prev) => ({ ...prev, [tx.id]: e.target.checked }))
                              }
                            />
                          </td>
                          <td className="py-2 px-3 text-slate-600 whitespace-nowrap">
                            {formatDate(tx.transaction_date)}
                          </td>
                          <td className="py-2 px-3 text-slate-800 max-w-xs truncate">
                            {tx.description}
                          </td>
                          <td className="py-2 px-3">
                            <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                              {CATEGORY_LABELS[tx.category] || tx.category}
                            </span>
                          </td>
                          <td className="py-2 px-3 text-right font-medium text-orange-600">
                            {formatCurrency(Math.abs(tx.amount))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-slate-200 bg-slate-50">
                        <td colSpan={4} className="py-2 px-3 font-semibold text-slate-700">
                          Total sélectionné
                        </td>
                        <td className="py-2 px-3 text-right font-bold text-slate-900">
                          {formatCurrency(txSelectedTotal)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}

              {charges > 0 && (
                <div className="p-3 bg-sky-50 border border-sky-200 rounded-lg text-sm text-sky-700 flex items-center justify-between">
                  <span>Provisions versées {regulYear} ({formatCurrency(charges)} × 12 mois)</span>
                  <span className="font-bold">{formatCurrency(charges * 12)}</span>
                </div>
              )}

              {regulResult && (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="p-4 bg-sky-50 rounded-lg border border-sky-200">
                      <p className="text-xs text-sky-600 mb-1">Provisions versées</p>
                      <p className="text-xl font-bold text-sky-800" style={{ fontFamily: "Manrope" }}>
                        {formatCurrency(regulResult.provisionnees)}
                      </p>
                    </div>
                    <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <p className="text-xs text-slate-600 mb-1">Charges réelles</p>
                      <p className="text-xl font-bold text-slate-800" style={{ fontFamily: "Manrope" }}>
                        {formatCurrency(regulResult.reelles)}
                      </p>
                    </div>
                    <div className={`p-4 rounded-lg border ${
                      regulResult.solde > 0 ? "bg-orange-50 border-orange-200"
                      : regulResult.solde < 0 ? "bg-navy-50 border-navy-200"
                      : "bg-slate-50 border-slate-200"
                    }`}>
                      <p className="text-xs text-slate-600 mb-1">Solde</p>
                      <p className={`text-xl font-bold ${
                        regulResult.solde > 0 ? "text-orange-700"
                        : regulResult.solde < 0 ? "text-navy-700"
                        : "text-slate-700"
                      }`} style={{ fontFamily: "Manrope" }}>
                        {regulResult.solde >= 0 ? "+" : ""}
                        {formatCurrency(regulResult.solde)}
                      </p>
                    </div>
                  </div>

                  <div className={`flex items-start gap-3 p-4 rounded-lg ${
                    regulResult.solde > 0 ? "bg-orange-50 border border-orange-200"
                    : regulResult.solde < 0 ? "bg-navy-50 border-navy-200"
                    : "bg-slate-50 border border-slate-200"
                  }`}>
                    {regulResult.solde > 0
                      ? <AlertCircle className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                      : <CheckCircle2 className="w-5 h-5 text-navy-600 flex-shrink-0 mt-0.5" />}
                    <div className="flex-1">
                      <p className={`font-semibold ${
                        regulResult.solde > 0 ? "text-orange-800"
                        : regulResult.solde < 0 ? "text-navy-800"
                        : "text-slate-700"
                      }`}>
                        {regulResult.solde > 0
                          ? `${tenant.name} doit un complément de ${formatCurrency(regulResult.solde)}`
                          : regulResult.solde < 0
                          ? `Remboursement de ${formatCurrency(Math.abs(regulResult.solde))} à ${tenant.name}`
                          : "Régulation équilibrée — aucun ajustement nécessaire"}
                      </p>
                      <p className="text-sm text-slate-500 mt-1">
                        {regulResult.solde > 0
                          ? "Les charges réelles dépassent les provisions versées."
                          : regulResult.solde < 0
                          ? "Les provisions versées excèdent les charges réelles."
                          : "Provisions = charges réelles."}
                      </p>
                    </div>
                  </div>

                  <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="w-full bg-navy-900 hover:bg-navy-800 gap-2">
                        <Mail className="w-5 h-5" />
                        Envoyer la régulation par email
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle style={{ fontFamily: "Manrope" }}>
                          Envoyer la régulation à {tenant.name}
                        </DialogTitle>
                        <DialogDescription>
                          {tenant.email
                            ? `L\'email sera envoyé à ${tenant.email}`
                            : "⚠️ Ce locataire n\'a pas d\'adresse email enregistrée dans Notion."}
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div className="p-4 bg-slate-50 rounded-lg space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-slate-500">Provisions versées</span>
                            <span className="font-medium">{formatCurrency(regulResult.provisionnees)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-500">Charges réelles {regulYear}</span>
                            <span className="font-medium">{formatCurrency(regulResult.reelles)}</span>
                          </div>
                          <div className="flex justify-between border-t border-slate-200 pt-2 mt-2">
                            <span className="font-semibold">Solde</span>
                            <span className={`font-bold ${regulResult.solde > 0 ? "text-orange-600" : "text-navy-700"}`}>
                              {regulResult.solde >= 0 ? "+" : ""}{formatCurrency(regulResult.solde)}
                            </span>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email-note">Message complémentaire (optionnel)</Label>
                          <textarea
                            id="email-note"
                            className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-800 resize-none focus:outline-none focus:ring-2 focus:ring-navy-500"
                            rows={3}
                            placeholder="Ex: Le paiement du complément est à effectuer avant le 30 du mois..."
                            value={emailNote}
                            onChange={(e) => setEmailNote(e.target.value)}
                          />
                        </div>
                        <div className="flex justify-end gap-3">
                          <Button variant="outline" onClick={() => setEmailDialogOpen(false)}>
                            Annuler
                          </Button>
                          <Button
                            className="bg-navy-900 hover:bg-navy-800 gap-2"
                            onClick={handleSendEmail}
                            disabled={!tenant.email || emailLoading}
                          >
                            {emailLoading
                              ? <Loader2 className="w-4 h-4 animate-spin" />
                              : <Send className="w-4 h-4" />}
                            Envoyer l\'email
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {!selectedTenantId && !loading && tenants.length > 0 && (
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle style={{ fontFamily: "Manrope" }}>Récapitulatif — tous les locataires</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-semibold text-slate-600">Locataire</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-600 hidden md:table-cell">Adresse</th>
                    <th className="text-right py-3 px-4 font-semibold text-slate-600">Loyer HC</th>
                    <th className="text-right py-3 px-4 font-semibold text-sky-600">Charges</th>
                    <th className="text-right py-3 px-4 font-semibold text-navy-700">Total CC</th>
                    <th className="py-3 px-4" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {tenants.map((t) => {
                    const tc = t.charges_provisionnees || 0;
                    return (
                      <tr
                        key={t.id}
                        className="hover:bg-slate-50 cursor-pointer"
                        onClick={() => setSelectedTenantId(t.id)}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center">
                              <span className="text-slate-600 font-semibold text-xs">
                                {t.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <span className="font-medium text-slate-900">{t.name}</span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-slate-500 hidden md:table-cell max-w-xs truncate">
                          {t.property_address}
                        </td>
                        <td className="py-3 px-4 text-right font-medium text-slate-900">
                          {formatCurrency(t.rent_amount)}
                        </td>
                        <td className="py-3 px-4 text-right font-medium text-sky-700">
                          {tc > 0 ? formatCurrency(tc) : <span className="text-slate-300">—</span>}
                        </td>
                        <td className="py-3 px-4 text-right font-bold text-navy-700">
                          {formatCurrency(t.rent_amount + tc)}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setSelectedTenantId(t.id); }}>
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-slate-200 bg-slate-50">
                    <td className="py-3 px-4 font-bold text-slate-700" colSpan={2}>Total</td>
                    <td className="py-3 px-4 text-right font-bold text-slate-900">{formatCurrency(totalLoyersMois)}</td>
                    <td className="py-3 px-4 text-right font-bold text-sky-700">{formatCurrency(totalChargesMois)}</td>
                    <td className="py-3 px-4 text-right font-bold text-navy-700">
                      {formatCurrency(totalLoyersMois + totalChargesMois)}
                    </td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
